import { usePageSEO } from '@/hooks/usePageSEO';
import Header from '@/components/feature/Header';
import Footer from '@/components/feature/Footer';
import Hero from './components/Hero';
import Services from './components/Services';
import Process from './components/Process';
import WhyUs from './components/WhyUs';
import Testimonials from './components/Testimonials';
import Zones from './components/Zones';
import FAQ from './components/FAQ';
import QuoteForm from './components/QuoteForm';
import CTAFinal from './components/CTAFinal';
import Empathy from './components/Empathy';

const localBusinessSchema = {
  '@context': 'https://schema.org',
  '@type': ['LocalBusiness', 'ProfessionalService'],
  '@id': 'https://couvreur-levis.com/#business',
  name: 'Couvreur-Lévis.com',
  alternateName: 'Couvreur Lévis',
  description: 'Plateforme de mise en relation entre propriétaires et couvreurs certifiés RBQ à Lévis, Québec. Soumission gratuite en 2h ouvrables.',
  url: 'https://couvreur-levis.com',
  telephone: '+1-418-476-9950',
  email: 'contact@couvreur-levis.com',
  priceRange: '$$-$$$',
  currenciesAccepted: 'CAD',
  paymentAccepted: 'Cash, Credit Card, Interac',
  slogan: 'Plateforme québécoise de mise en relation avec des couvreurs certifiés RBQ',
  address: {
    '@type': 'PostalAddress',
    addressLocality: 'Lévis',
    addressRegion: 'QC',
    postalCode: 'G6V',
    addressCountry: {
      '@type': 'Country',
      name: 'Canada',
    },
  },
  geo: {
    '@type': 'GeoCoordinates',
    latitude: 46.7903,
    longitude: -71.1844,
  },
  openingHoursSpecification: [
    {
      '@type': 'OpeningHoursSpecification',
      dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'],
      opens: '07:00',
      closes: '18:00',
    },
    {
      '@type': 'OpeningHoursSpecification',
      dayOfWeek: ['Saturday', 'Sunday'],
      opens: '08:00',
      closes: '16:00',
    },
  ],
  areaServed: [
    { '@type': 'City', name: 'Lévis' },
    { '@type': 'City', name: 'Saint-Romuald' },
    { '@type': 'City', name: 'Saint-Nicolas' },
    { '@type': 'City', name: 'Charny' },
    { '@type': 'City', name: 'Pintendre' },
    { '@type': 'City', name: 'Saint-Jean-Chrysostome' },
    { '@type': 'City', name: 'Lauzon' },
    { '@type': 'City', name: 'Saint-Étienne-de-Lauzon' },
  ],
  hasOfferCatalog: {
    '@type': 'OfferCatalog',
    name: 'Services de toiture à Lévis',
    itemListElement: [
      { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Réfection complète de toiture' } },
      { '@type': 'Offer', itemOffered: { '@type': 'Service', name: "Réparation et infiltration d'eau" } },
      { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Toit plat - Membrane élastomère' } },
      { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Toiture en tôle et métal' } },
      { '@type': 'Offer', itemOffered: { '@type': 'Service', name: "Pose de bardeau d'asphalte" } },
      { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Inspection de toiture' } },
      { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Déneigement de toiture' } },
    ],
  },
};

const websiteSchema = {
  '@context': 'https://schema.org',
  '@type': 'WebSite',
  '@id': 'https://couvreur-levis.com/#website',
  url: 'https://couvreur-levis.com',
  name: 'Couvreur-Lévis.com',
  description: 'Plateforme québécoise de mise en relation entre clients et couvreurs RBQ à Lévis',
  inLanguage: 'fr-CA',
  publisher: {
    '@type': 'Organization',
    name: 'Couvreur-Lévis.com',
    areaServed: {
      '@type': 'AdministrativeArea',
      name: 'Québec, Canada',
    },
  },
};

const faqSchema = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  inLanguage: 'fr-CA',
  mainEntity: [
    {
      '@type': 'Question',
      name: 'Combien coûte une nouvelle toiture à Lévis en 2026 ?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: "Ça dépend de la superficie, des matériaux et de l'accessibilité. En moyenne, compte entre 8 000 $ et 18 000 $ pour une maison unifamiliale standard. La seule façon d'avoir un prix exact, c'est qu'on vienne inspecter. La soumission est gratuite et sans engagement.",
      },
    },
    {
      '@type': 'Question',
      name: "Combien de temps dure une toiture en bardeau d'asphalte ?",
      acceptedAnswer: {
        '@type': 'Answer',
        text: "Un bardeau d'asphalte de qualité bien installé dure entre 15 et 25 ans dans notre climat québécois. L'entretien régulier, le déneigement et la ventilation adéquate peuvent allonger cette durée de plusieurs années.",
      },
    },
    {
      '@type': 'Question',
      name: 'Êtes-vous certifiés et assurés ?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: "Oui, absolument. Nous détenons une licence valide de la Régie du bâtiment du Québec et une assurance responsabilité civile complète. On vous présente les documents sur demande, sans problème.",
      },
    },
    {
      '@type': 'Question',
      name: 'Offrez-vous une garantie écrite ?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: "Oui. Tous nos travaux sont couverts par une garantie écrite de 10 ans sur la main-d'œuvre et les matériaux. C'est signé, daté, et envoyé par courriel. Pas de promesse en l'air.",
      },
    },
    {
      '@type': 'Question',
      name: 'Travaillez-vous en hiver ?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: "On fait des réparations d'urgence et des inspections en hiver, oui. Pour les réfections complètes, on attend généralement le printemps quand les températures le permettent — surtout pour le bardeau d'asphalte qui a besoin de chaleur pour sceller correctement.",
      },
    },
    {
      '@type': 'Question',
      name: "Qu'est-ce qui est inclus dans la soumission gratuite ?",
      acceptedAnswer: {
        '@type': 'Answer',
        text: "L'inspection sur place, un rapport photo détaillé de l'état de votre toiture, une soumission écrite avec prix détaillés par poste, les options de matériaux, et un échéancier de travail. Aucun frais caché, aucune pression.",
      },
    },
  ],
};

export default function HomePage() {
  usePageSEO({
    title: 'Couvreur à Lévis | Soumission gratuite en 2h | RBQ',
    description: 'Trouvez un couvreur certifié RBQ à Lévis. Soumission gratuite en 2h ouvrables. Réseau de couvreurs partenaires. Appelez le 418-476-9950.',
    urlPath: '/',
    keywords: 'couvreur Lévis, toiture Lévis, réparation toiture, bardeau asphalte, toiture métal, soumission gratuite',
    ogImage: '/og/og-home.jpg',
    ogImageAlt: 'Couvreur à Lévis - Réseau de couvreurs RBQ certifiés',
    jsonLdSchema: [localBusinessSchema, websiteSchema, faqSchema],
  });

  return (
    <div className="font-inter min-h-screen bg-white">
      <Header />
      <main>
        <Hero />
        <Empathy />
        <Services />
        <Process />
        <WhyUs />
        <Testimonials />
        <Zones />
        <FAQ />
        <QuoteForm />
        <CTAFinal />
      </main>
      <Footer />
    </div>
  );
}